import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Stack,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
  useMediaQuery,
  useTheme,
  Alert,
  Snackbar
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Palette,
  Refresh,
  Download,
  ShoppingCart,
  ColorLens,
  Brush,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// Sample color palettes
const COLOR_PALETTES = {
  football: [
    { name: "Team Red", hex: "#d32f2f", rgb: "rgb(211, 47, 47)" },
    { name: "Navy Blue", hex: "#1976d2", rgb: "rgb(25, 118, 210)" },
    { name: "Forest Green", hex: "#388e3c", rgb: "rgb(56, 142, 60)" },
    { name: "Golden Yellow", hex: "#f57c00", rgb: "rgb(245, 124, 0)" },
    { name: "Pure White", hex: "#ffffff", rgb: "rgb(255, 255, 255)" },
    { name: "Jet Black", hex: "#212121", rgb: "rgb(33, 33, 33)" }
  ],
  basketball: [
    { name: "Lakers Purple", hex: "#7b1fa2", rgb: "rgb(123, 31, 162)" },
    { name: "Celtics Green", hex: "#388e3c", rgb: "rgb(56, 142, 60)" },
    { name: "Bulls Red", hex: "#d32f2f", rgb: "rgb(211, 47, 47)" },
    { name: "Warriors Blue", hex: "#1976d2", rgb: "rgb(25, 118, 210)" },
    { name: "Heat Orange", hex: "#f57c00", rgb: "rgb(245, 124, 0)" },
    { name: "Pure White", hex: "#ffffff", rgb: "rgb(255, 255, 255)" }
  ],
  cricket: [
    { name: "India Blue", hex: "#1976d2", rgb: "rgb(25, 118, 210)" },
    { name: "Pakistan Green", hex: "#388e3c", rgb: "rgb(56, 142, 60)" },
    { name: "Australia Gold", hex: "#f57c00", rgb: "rgb(245, 124, 0)" },
    { name: "England Red", hex: "#d32f2f", rgb: "rgb(211, 47, 47)" },
    { name: "Pure White", hex: "#ffffff", rgb: "rgb(255, 255, 255)" },
    { name: "Jet Black", hex: "#212121", rgb: "rgb(33, 33, 33)" }
  ],
  hockey: [
    { name: "Maple Leaf Red", hex: "#d32f2f", rgb: "rgb(211, 47, 47)" },
    { name: "Bruins Black", hex: "#212121", rgb: "rgb(33, 33, 33)" },
    { name: "Rangers Blue", hex: "#1976d2", rgb: "rgb(25, 118, 210)" },
    { name: "Canadiens Red", hex: "#d32f2f", rgb: "rgb(211, 47, 47)" },
    { name: "Pure White", hex: "#ffffff", rgb: "rgb(255, 255, 255)" },
    { name: "Silver Grey", hex: "#757575", rgb: "rgb(117, 117, 117)" }
  ]
};

// Sample SVG designs for each category
const SVG_DESIGNS = {
  football: {
    id: 1,
    name: "Classic Football Kit",
    svg: `
      <svg width="300" height="400" viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg">
        <!-- Jersey base -->
        <rect x="50" y="50" width="200" height="300" rx="20" fill="PRIMARY_COLOR" stroke="SECONDARY_COLOR" stroke-width="3"/>
        <!-- Collar -->
        <circle cx="150" cy="80" r="15" fill="SECONDARY_COLOR"/>
        <!-- Sleeves -->
        <rect x="30" y="100" width="40" height="120" rx="20" fill="PRIMARY_COLOR" stroke="SECONDARY_COLOR" stroke-width="2"/>
        <rect x="230" y="100" width="40" height="120" rx="20" fill="PRIMARY_COLOR" stroke="SECONDARY_COLOR" stroke-width="2"/>
        <!-- Team logo area -->
        <circle cx="150" cy="200" r="40" fill="ACCENT_COLOR" opacity="0.8"/>
        <!-- Number area -->
        <rect x="120" y="250" width="60" height="80" rx="10" fill="SECONDARY_COLOR"/>
        <text x="150" y="300" text-anchor="middle" fill="PRIMARY_COLOR" font-size="48" font-weight="bold">10</text>
        <!-- Sponsor area -->
        <rect x="80" y="340" width="140" height="30" rx="5" fill="ACCENT_COLOR"/>
      </svg>
    `,
    colorableElements: [
      { id: "primary", name: "Main Jersey Color", defaultColor: "#1976d2" },
      { id: "secondary", name: "Accent Color", defaultColor: "#ffffff" },
      { id: "accent", name: "Logo & Sponsor Color", defaultColor: "#f57c00" }
    ]
  },
  basketball: {
    id: 4,
    name: "Pro Basketball Jersey",
    svg: `
      <svg width="300" height="400" viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg">
        <!-- Jersey base -->
        <rect x="50" y="50" width="200" height="300" rx="15" fill="PRIMARY_COLOR" stroke="SECONDARY_COLOR" stroke-width="2"/>
        <!-- V-neck collar -->
        <path d="M 150 80 L 120 120 L 180 120 Z" fill="SECONDARY_COLOR"/>
        <!-- Sleeves -->
        <rect x="30" y="120" width="35" height="100" rx="15" fill="PRIMARY_COLOR" stroke="SECONDARY_COLOR" stroke-width="2"/>
        <rect x="235" y="120" width="35" height="100" rx="15" fill="PRIMARY_COLOR" stroke="SECONDARY_COLOR" stroke-width="2"/>
        <!-- Team logo -->
        <circle cx="150" cy="180" r="35" fill="ACCENT_COLOR"/>
        <!-- Player number -->
        <rect x="125" y="230" width="50" height="70" rx="8" fill="SECONDARY_COLOR"/>
        <text x="150" y="275" text-anchor="middle" fill="PRIMARY_COLOR" font-size="36" font-weight="bold">23</text>
        <!-- Player name -->
        <rect x="100" y="310" width="100" height="25" rx="5" fill="ACCENT_COLOR"/>
        <text x="150" y="327" text-anchor="middle" fill="SECONDARY_COLOR" font-size="14" font-weight="bold">JORDAN</text>
      </svg>
    `,
    colorableElements: [
      { id: "primary", name: "Main Jersey Color", defaultColor: "#d32f2f" },
      { id: "secondary", name: "Accent Color", defaultColor: "#ffffff" },
      { id: "accent", name: "Logo & Name Color", defaultColor: "#212121" }
    ]
  },
  cricket: {
    id: 6,
    name: "Traditional Cricket Kit",
    svg: `
      <svg width="300" height="400" viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg">
        <!-- Jersey base -->
        <rect x="50" y="50" width="200" height="300" rx="25" fill="PRIMARY_COLOR" stroke="SECONDARY_COLOR" stroke-width="3"/>
        <!-- Collar -->
        <rect x="130" y="60" width="40" height="25" rx="12" fill="SECONDARY_COLOR"/>
        <!-- Sleeves -->
        <rect x="25" y="100" width="45" height="130" rx="20" fill="PRIMARY_COLOR" stroke="SECONDARY_COLOR" stroke-width="2"/>
        <rect x="230" y="100" width="45" height="130" rx="20" fill="PRIMARY_COLOR" stroke="SECONDARY_COLOR" stroke-width="2"/>
        <!-- Team logo -->
        <circle cx="150" cy="190" r="45" fill="ACCENT_COLOR" opacity="0.9"/>
        <!-- Player number -->
        <rect x="115" y="250" width="70" height="90" rx="15" fill="SECONDARY_COLOR"/>
        <text x="150" y="305" text-anchor="middle" fill="PRIMARY_COLOR" font-size="52" font-weight="bold">7</text>
        <!-- Sponsor -->
        <rect x="70" y="350" width="160" height="35" rx="8" fill="ACCENT_COLOR"/>
      </svg>
    `,
    colorableElements: [
      { id: "primary", name: "Main Jersey Color", defaultColor: "#388e3c" },
      { id: "secondary", name: "Accent Color", defaultColor: "#ffffff" },
      { id: "accent", name: "Logo & Sponsor Color", defaultColor: "#f57c00" }
    ]
  },
  hockey: {
    id: 7,
    name: "Ice Hockey Jersey",
    svg: `
      <svg width="300" height="400" viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg">
        <!-- Jersey base -->
        <rect x="50" y="50" width="200" height="300" rx="20" fill="PRIMARY_COLOR" stroke="SECONDARY_COLOR" stroke-width="3"/>
        <!-- Shoulder pads -->
        <rect x="40" y="80" width="50" height="40" rx="20" fill="SECONDARY_COLOR"/>
        <rect x="210" y="80" width="50" height="40" rx="20" fill="SECONDARY_COLOR"/>
        <!-- Sleeves -->
        <rect x="20" y="130" width="50" height="120" rx="25" fill="PRIMARY_COLOR" stroke="SECONDARY_COLOR" stroke-width="2"/>
        <rect x="230" y="130" width="50" height="120" rx="25" fill="PRIMARY_COLOR" stroke="SECONDARY_COLOR" stroke-width="2"/>
        <!-- Team logo -->
        <circle cx="150" cy="200" r="50" fill="ACCENT_COLOR"/>
        <!-- Player number -->
        <rect x="110" y="260" width="80" height="100" rx="20" fill="SECONDARY_COLOR"/>
        <text x="150" y="320" text-anchor="middle" fill="PRIMARY_COLOR" font-size="56" font-weight="bold">99</text>
        <!-- Player name -->
        <rect x="90" y="370" width="120" height="30" rx="8" fill="ACCENT_COLOR"/>
        <text x="150" y="390" text-anchor="middle" fill="SECONDARY_COLOR" font-size="16" font-weight="bold">GRETZKY</text>
      </svg>
    `,
    colorableElements: [
      { id: "primary", name: "Main Jersey Color", defaultColor: "#d32f2f" },
      { id: "secondary", name: "Accent Color", defaultColor: "#ffffff" },
      { id: "accent", name: "Logo & Name Color", defaultColor: "#212121" }
    ]
  }
};

function ColorSelection() {
  const { designId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const svgRef = useRef(null);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [category, setCategory] = useState('');
  const [colors, setColors] = useState({});
  const [selectedSize, setSelectedSize] = useState('M');
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [showPreview, setShowPreview] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (location.state?.design && location.state?.category) {
      const design = SVG_DESIGNS[location.state.category];
      setSelectedDesign(design);
      setCategory(location.state.category);
      
      // Initialize colors with defaults
      const initialColors = {};
      design.colorableElements.forEach(element => {
        initialColors[element.id] = element.defaultColor;
      });
      setColors(initialColors);
    } else {
      // Fallback - try to find design by ID
      const foundDesign = Object.values(SVG_DESIGNS).find(d => d.id === parseInt(designId));
      if (foundDesign) {
        const foundCategory = Object.keys(SVG_DESIGNS).find(key => SVG_DESIGNS[key].id === parseInt(designId));
        setSelectedDesign(foundDesign);
        setCategory(foundCategory);
        
        const initialColors = {};
        foundDesign.colorableElements.forEach(element => {
          initialColors[element.id] = element.defaultColor;
        });
        setColors(initialColors);
      }
    }
  }, [designId, location.state]);

  const handleColorChange = (elementId, color) => {
    setColors(prev => ({
      ...prev,
      [elementId]: color
    }));
  };

  const resetColors = () => {
    const initialColors = {};
    selectedDesign.colorableElements.forEach(element => {
      initialColors[element.id] = element.defaultColor;
    });
    setColors(initialColors);
  };

  const getColoredSVG = () => {
    if (!selectedDesign) return '';
    
    let coloredSVG = selectedDesign.svg;
    Object.keys(colors).forEach(colorKey => {
      const colorValue = colors[colorKey];
      const placeholder = colorKey.toUpperCase() + '_COLOR';
      coloredSVG = coloredSVG.replace(new RegExp(placeholder, 'g'), colorValue);
    });
    return coloredSVG;
  };

  const handleContinue = () => {
    if (!selectedSize || selectedQuantity < 1) {
      setSnackbarMessage('Please select size and quantity');
      setSnackbarOpen(true);
      return;
    }
    
    navigate('/customize-logo', {
      state: {
        design: selectedDesign,
        category,
        colors,
        size: selectedSize,
        quantity: selectedQuantity
      }
    });
  };

  const renderBreadcrumbs = () => (
    <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
      <MuiLink component={Link} to="/" color="inherit">
        Home
      </MuiLink>
      <MuiLink component={Link} to={`/${category}`} color="inherit">
        {category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Category'}
      </MuiLink>
      <Typography color="text.primary">
        Customize Colors
      </Typography>
    </Breadcrumbs>
  );

  const renderDesignPreview = () => (
    <Grid item xs={12} lg={8}>
      <Card sx={{ height: 'fit-content' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2">
              Design Preview
            </Typography>
            <Button
              startIcon={showPreview ? <VisibilityOff /> : <Visibility />}
              onClick={() => setShowPreview(!showPreview)}
              variant="outlined"
              size="small"
            >
              {showPreview ? 'Hide' : 'Show'} Preview
            </Button>
          </Box>
          
          {showPreview && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px',
                position: 'relative',
                background: 'transparent',
                overflow: 'hidden',
                borderRadius: 2,
                p: 2,
                '& > div': {
                  position: 'relative',
                  zIndex: 1,
                  filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.2))'
                }
              }}
            >
              <div
                ref={svgRef}
                dangerouslySetInnerHTML={{ __html: getColoredSVG() }}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </Box>
          )}
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              startIcon={<Refresh />}
              onClick={resetColors}
              variant="outlined"
              sx={{ mr: 2 }}
            >
              Reset Colors
            </Button>
            <Button
              startIcon={<Download />}
              variant="outlined"
              onClick={() => {
                // In a real app, this would download the SVG
                setSnackbarMessage('Download feature coming soon!');
                setSnackbarOpen(true);
              }}
            >
              Download Preview
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderColorControls = () => (
    <Grid item xs={12} lg={4}>
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              <ColorLens sx={{ mr: 1, verticalAlign: 'middle' }} />
              Customize Colors
            </Typography>
            
            {selectedDesign?.colorableElements.map((element) => (
              <Box key={element.id} sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {element.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input
                    type="color"
                    value={colors[element.id] || '#000000'}
                    onChange={(e) => handleColorChange(element.id, e.target.value)}
                    style={{
                      width: '50px',
                      height: '40px',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {colors[element.id] || '#000000'}
                  </Typography>
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              <Brush sx={{ mr: 1, verticalAlign: 'middle' }} />
              Quick Color Palettes
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Popular team color combinations
            </Typography>
            
            <Stack spacing={1}>
              {COLOR_PALETTES[category]?.slice(0, 6).map((color, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                  onClick={() => {
                    // Apply this color to the first colorable element
                    const firstElement = selectedDesign?.colorableElements[0];
                    if (firstElement) {
                      handleColorChange(firstElement.id, color.hex);
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: '30px',
                      height: '30px',
                      bgcolor: color.hex,
                      borderRadius: '4px',
                      border: '1px solid #ddd'
                    }}
                  />
                  <Typography variant="body2">{color.name}</Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              <ShoppingCart sx={{ mr: 1, verticalAlign: 'middle' }} />
              Order Details
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Size</InputLabel>
                <Select
                  value={selectedSize}
                  label="Size"
                  onChange={(e) => setSelectedSize(e.target.value)}
                >
                  {['XS', 'S', 'M', 'L', 'XL', 'XXL'].map((size) => (
                    <MenuItem key={size} value={size}>{size}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel>Quantity</InputLabel>
                <Select
                  value={selectedQuantity}
                  label="Quantity"
                  onChange={(e) => setSelectedQuantity(e.target.value)}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((qty) => (
                    <MenuItem key={qty} value={qty}>{qty}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Button
              fullWidth
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={handleContinue}
            >
              Continue to Logo Customization
            </Button>
          </CardContent>
        </Card>
      </Stack>
    </Grid>
  );

  if (!selectedDesign) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Design Not Found
        </Typography>
        <Button component={Link} to="/" variant="contained">
          Back to Home
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {renderBreadcrumbs()}
      
      <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 4 }}>
        Customize Your {selectedDesign.name}
      </Typography>
      
      <Grid container spacing={4}>
        {renderDesignPreview()}
        {renderColorControls()}
      </Grid>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="info">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default ColorSelection;
